import { signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { html, list } from "../lib/bind";
import { labelRow } from "../lib/table";
import { formatMoney } from "../lib/money";
import { toast } from "../lib/toast";
import { buildBankingSubNav } from "./banking-subnav";
import { type TransferSuggestion, acceptTransfer, listTransferSuggestions } from "../services/transfer";

export function bankingTransfersView(): Node {
  const transferSuggestions = signal<TransferSuggestion[]>([]);

  function reloadTransferSuggestions(): void {
    listTransferSuggestions()
      .then((result) => {
        transferSuggestions.value = result;
      })
      .catch(() => undefined);
  }
  reloadTransferSuggestions();

  async function acceptTransferSuggestion(s: TransferSuggestion): Promise<void> {
    try {
      await acceptTransfer(s.transaction_a_id, s.transaction_b_id);
      toast.success("Marked as a transfer.");
      reloadTransferSuggestions();
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Accept failed");
    }
  }

  const fragment = html`<div class="view">
    <div class="card">
      <div class="card-label">Transfer suggestions</div>
      <p class="field-hint">
        Equal &amp; opposite amounts across two of your own accounts within a few days —
        e.g. a credit card payment leaving your current account and landing on the
        card's own statement.
      </p>
      <div class="data-table transfer-suggestions-table">
        <table>
          <thead>
            <tr>
              <th>Account A</th>
              <th>Account B</th>
              <th>Amount</th>
              <th>Gap</th>
              <th>Reason</th>
              <th></th>
            </tr>
          </thead>
          <tbody class="transfer-suggestions-body"></tbody>
        </table>
      </div>
    </div>
  </div>`;

  const viewEl = fragment.querySelector<HTMLElement>(".view");
  if (!viewEl) throw new Error("banking transfers view produced no .view element");
  viewEl.insertBefore(buildBankingSubNav("transfers"), viewEl.firstChild);

  const transferSuggestionsTable = fragment.querySelector<HTMLTableElement>(
    ".transfer-suggestions-table table",
  );
  const transferSuggestionsBody = fragment.querySelector<HTMLTableSectionElement>(
    ".transfer-suggestions-body",
  );
  if (!transferSuggestionsTable || !transferSuggestionsBody) {
    throw new Error("banking transfers view markup missing expected elements");
  }

  list(
    transferSuggestionsBody,
    transferSuggestions,
    (s) => `${s.transaction_a_id}-${s.transaction_b_id}`,
    (s) => {
      const row = html`<tr>
        <td>${s.transaction_a_account_name}: ${s.transaction_a_description} (${s.transaction_a_date})</td>
        <td>${s.transaction_b_account_name}: ${s.transaction_b_description} (${s.transaction_b_date})</td>
        <td>${formatMoney(s.transaction_a_amount)}</td>
        <td>${String(s.day_gap)}d</td>
        <td>${s.reason}</td>
        <td><button class="btn btn-primary accept-transfer-btn" type="button">Accept</button></td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("transfer suggestion row template produced no element");
      const acceptBtn = row.querySelector<HTMLButtonElement>(".accept-transfer-btn");
      acceptBtn?.addEventListener("click", () => {
        void acceptTransferSuggestion(s);
      });
      return labelRow(row, transferSuggestionsTable);
    },
  );

  return fragment;
}
